import React, { useContext, useEffect, useRef } from 'react'
import { string, number, oneOfType } from 'prop-types'
import { init, initConversionEventListener } from '@duplo/widget'
import { css, Global } from '@emotion/core'
import { isBrowser, pushGtmData } from '@jelly/utils'
import { SiteConfigContext } from '../SiteConfigProvider'

import { STAGES } from '@duplo/widget/lib/constants'

function rfqEventHandlerCallback(event) {
	pushGtmData({
		event: event.type,
	})
}

function useRfqWidgetEventListeners() {
	useEffect(() => {
		document.addEventListener('bzFirstInteraction', rfqEventHandlerCallback, false)
		document.addEventListener('bzConversion', rfqEventHandlerCallback, false)

		return () => {
			document.removeEventListener('bzFirstInteraction', rfqEventHandlerCallback, false)
			document.removeEventListener('bzConversion', rfqEventHandlerCallback, false)
		}
	}, [])
}

const WidgetCss = () => (
	<Global
		styles={css`
			.bzWidget {
				max-width: 100% !important;
			}
		`}
	/>
)

/**
 * this is an enhanced implementation of widget init which waits for the GTM datalayer to be ready to ensure that google
 * optimize is loaded in case there are any experiments running on the page.
 *
 * @param {object} widgetElement - DOM node for the widget loading code
 */
function setupWidgetInit(widgetElement) {
	const widgetInitCallback = (value) => {
		// if we're in a test variation for widget, add the feature key attr
		if (window.goRfqWidgetFeatureKey) {
			widgetElement.setAttribute('data-bzwidget-feature-key', window.goRfqWidgetFeatureKey)
		}

		init()
		initConversionEventListener()
	}

	// this is a backup in case some adblockers prevent the dataLayer.hide.end callback to execute below
	const widgetInitBackupTimeout = setTimeout(() => {
		widgetInitCallback()
	}, 4100)

	/* modified version of this: https://support.google.com/optimize/answer/9059383#delayed-page-nitialization
	check if there is a callback set on the datalayer, if yes update it to include our callback, otherwise
	datalayer has already loaded and we can safely call our callback */
	let existingHideEnd = window.dataLayer && window.dataLayer.hide && window.dataLayer.hide.end

	if (typeof existingHideEnd === 'function') {
		window.dataLayer.hide.end = () => {
			clearTimeout(widgetInitBackupTimeout)
			widgetInitCallback()
			existingHideEnd()
		}
	} else {
		clearTimeout(widgetInitBackupTimeout)
		widgetInitCallback()
	}
}

/**
 * This component is for the RFQ Widget code
 * Will create a reference to load the rfq widget
 *
 * @param {string} widgetCategoryId Category id
 *
 */
export const RfqWidget = ({ className, id, publisherId, widgetCategoryId }) => {
	const element = useRef(null)
	const {
		siteConstants: { RFQ_PUBLISHER_ID, WIDGET_ENV, RFQ_WIDGET_THEME },
	} = useContext(SiteConfigContext)

	const pubId = publisherId || RFQ_PUBLISHER_ID

	useEffect(() => {
		/**
		 * After we have an element with categoryId
		 * we can call the init and initConversionEventListener functionality,
		 * the latter to broadcast the conversaion event from within the iframe
		 */
		if (element.current != null) {
			setupWidgetInit(element.current)
		}
	}, [])

	useRfqWidgetEventListeners()

	// If categoryId is not there we dont need a container
	return widgetCategoryId ? (
		<>
			<WidgetCss />
			<div
				ref={element}
				className={className}
				id={id}
				data-bzwidget
				data-bzwidget-stage={STAGES[WIDGET_ENV]}
				data-bzwidget-category-id={widgetCategoryId}
				data-bzwidget-pub-id={pubId}
				data-bzwidget-lazy-load="true"
				data-bzwidget-lazy-load-offset="200"
				data-bzwidget-hide-border
				data-bzwidget-color-palette-name={RFQ_WIDGET_THEME || ''}
			/>
		</>
	) : null
}

RfqWidget.propTypes = {
	widgetCategoryId: oneOfType([string, number]),
}

/**
 * HOC functionality looks in the content for widget Ids
 * and set the parameter for v4 Widgets
 *
 * @param {Component} Component React Component
 */
export function withRfqWidget(Component) {
	return (props) => {
		const {
			siteConstants: { RFQ_PUBLISHER_ID, WIDGET_ENV, RFQ_WIDGET_THEME },
		} = useContext(SiteConfigContext)

		useEffect(() => {
			const rfqWidgetSetup = () => {
				// Gather all cases where rfq is called
				const rfqWidgets = [...document.getElementsByClassName('bz-form-widget')]

				if (rfqWidgets.length > 0) {
					// If there is elements we add the attributes nedded to load the widget
					rfqWidgets.forEach((element) => {
						const widgetCategoryId = element.getAttribute('data-bz-category-id')
						if (widgetCategoryId) {
							element.setAttribute('data-bzwidget', true)
							element.setAttribute('data-bzwidget-stage', STAGES[WIDGET_ENV])
							element.setAttribute('data-bzwidget-category-id', widgetCategoryId)
							element.setAttribute('data-bzwidget-pub-id', RFQ_PUBLISHER_ID)
							element.setAttribute('data-bzwidget-hide-border', true)
							element.setAttribute('data-bzwidget-lazy-load', true)
							element.setAttribute('data-bzwidget-lazy-load-offset', 200)
							element.setAttribute('data-bzwidget-color-palette-name', RFQ_WIDGET_THEME || '')
							// Removal of the class in case that we need to call initWidget again
							element.classList.remove('bz-form-widget')
							element.classList.add('widget-post-init')

							setupWidgetInit(element)
						}
					})
					window.removeEventListener('load', rfqWidgetSetup)
				}
			}
			isBrowser() && document.readyState === 'complete'
				? rfqWidgetSetup()
				: window.addEventListener('load', rfqWidgetSetup)
		}, [RFQ_WIDGET_THEME, RFQ_PUBLISHER_ID, WIDGET_ENV])

		useRfqWidgetEventListeners()

		return (
			<>
				<WidgetCss />
				<Component {...props} />
			</>
		)
	}
}

export default RfqWidget
